import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '@/lib/store';
import { generate } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';

export function PreviewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { previews, setPreviews, selectedVersion, setSelectedVersion } = useStore();
  const [loading, setLoading] = useState(!previews.length);
  const [error, setError] = useState<string | null>(null);
  const [previewModal, setPreviewModal] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      navigate('/generate');
      return;
    }

    if (previews.length === 0) {
      loadSession();
    }
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const session = await generate.get(sessionId!);
      if (session.status === 'expired') {
        setError('This session has expired. Please start over.');
        return;
      }
      setPreviews(session.previews);
      if (session.selectedVersion) {
        setSelectedVersion(session.selectedVersion);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (version: string) => {
    setSelectedVersion(version);
    try {
      await generate.select(sessionId!, version);
    } catch (err) {
      console.error('Failed to save selection:', err);
    }
  };

  const handleContinue = () => {
    if (selectedVersion && sessionId) {
      navigate(`/checkout/${sessionId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto text-primary-500" />
          <p className="mt-4 text-gray-600">Loading your previews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => navigate('/generate')} className="btn-primary">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Design</h1>
          <p className="text-gray-600">
            We've created 3 unique variations. Pick your favorite to continue.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {previews.map((preview) => (
            <div
              key={preview.id}
              className={`
                card cursor-pointer transition-all
                ${selectedVersion === preview.id
                  ? 'ring-4 ring-primary-500 border-primary-500'
                  : 'hover:shadow-lg'
                }
              `}
              onClick={() => handleSelect(preview.id)}
            >
              <div className="relative mb-4">
                <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={preview.previewHtml}
                    className="w-full h-full pointer-events-none"
                    title={`Preview ${preview.id}`}
                    style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }}
                  />
                </div>
                {selectedVersion === preview.id && (
                  <div className="absolute top-2 right-2 w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-900">
                  Version {preview.id.replace('v', '')}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewModal(preview.id);
                  }}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Full Preview
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            disabled={!selectedVersion}
            className="btn-primary text-lg px-8 py-4"
          >
            Continue to Checkout
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>

        <p className="text-center text-gray-500 text-sm mt-4">
          Previews include a watermark. The final deployed version will be clean.
        </p>
      </div>

      {/* Full Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">
                Full Preview - Version {previewModal.replace('v', '')}
              </h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleSelect(previewModal)}
                  className={`btn-primary text-sm ${selectedVersion === previewModal ? 'bg-green-500' : ''}`}
                >
                  {selectedVersion === previewModal ? 'Selected' : 'Select This Version'}
                </button>
                <button
                  onClick={() => setPreviewModal(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                srcDoc={previews.find((p) => p.id === previewModal)?.previewHtml}
                className="w-full h-full min-h-[70vh]"
                title="Full Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
